/**
 * Publishers inventory rollup (flank#4643). The bundle -> Ribeye Publisher
 * mapping itself now happens server-side (ribeye-api's `ribeye_publisher`
 * report dimension, fixing the flank#4644 conflict: that ticket's raw-
 * inventory permission gate strips raw `app_name` rows for every non-admin
 * caller, which used to break this view — built client-side against those
 * same raw rows — for exactly the users it's supposed to default-serve).
 * `ribeye_publisher` reuses the exact bundle->publisher matching legacy
 * Reporting's `BasePublisherReportService.processAppReport` uses
 * (`BeeswaxPublisherService.getPublishersWithBundles`) and returns grouped
 * rows keyed by a bucket string: `publisher-<id>` for a mapped bundle,
 * `unmapped` for a real-but-unmapped one, `unknown` for a junk/blank value,
 * `gam-self:<name>` for an uncurated GAM bundle shown under GAM's own
 * reported name instead of Unmapped (Matt, ribeye-api#2615 — GAM is Bonneville's own
 * ad server, so there's nothing to resolve against the directory). Known
 * social apps (Facebook, X, …) never reach any bucket at all — dropped
 * server-side before bucketing, since they have no publisher concept. See
 * ribeye-api's `app/Services/FlankReporting/publisherRollup.ts`, which
 * this file's bucket-key handling must stay in sync with.
 *
 * This file's remaining job: turn those pre-grouped totals (+ the Publisher
 * directory) into `PublisherInventoryRow[]`, and attach the expanded row's
 * per-bundle/DSP breakdown from the one raw dimension still fetched for it
 * (`app_name`, still gated by `can_view_raw_inventory`, unchanged) — the only
 * remaining reason this file still needs the bundle index at all.
 *
 * Domains are deliberately NOT folded in here (Lucas, review): `ribeye_domain`
 * reads the DOMAIN fact and `ribeye_publisher` reads the APP fact — for GAM
 * these are the same delivery sliced two different ways, so summing both into
 * one set of totals double-counts it (~130M in each fact vs ~134M actually
 * delivered). This view's job (#4643) is app-bundle -> Ribeye Publisher
 * mapping; domain-level inventory is a separate concern for a future ticket,
 * not a toggle bolted onto this one.
 */
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { BLANK_NAME_SENTINELS } from '../format';

export interface RibeyePublisher {
  id: number | string;
  name: string;
  logo: string | null;
  bundles: string[];
}

/** Warehouse sentinel for "no value here" seen on other dimensions (e.g. CITY_CODE) — not caught by BLANK_NAME_SENTINELS, which only covers the word-shaped placeholders. */
const JUNK_VALUE_SENTINELS = new Set(['-1']);

/**
 * True when a raw app_name/domain value carries no usable name at all —
 * blank, a word-shaped placeholder ("Unknown", "(none)", …) or the "-1"
 * sentinel. These are the rows the ticket wants merged into one "Unknown /
 * Pending mapping" bucket rather than listed (unmapped) under their own raw
 * value. Mirrored server-side in ribeye-api's `publisherRollup.ts`
 * (`isJunkBundleName`) for the `app_name` side of this same test; this copy
 * still runs client-side for `ribeye_domain` values, which the server never
 * buckets (a plain passthrough — see module doc comment above).
 */
export const isJunkInventoryName = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  if (!s) return true;
  return BLANK_NAME_SENTINELS.has(s.toLowerCase()) || JUNK_VALUE_SENTINELS.has(s);
};

/**
 * bundle name -> publisher, matching `processAppReport`'s `appToPublisherName`
 * exactly: `publishers.flatMap(p => p.bundles.map(bundle => [bundle, p]))`
 * then `new Map(...)`, so a bundle claimed by two publishers resolves to
 * whichever one is later in the list (same tie-break as legacy). Used here
 * ONLY to attribute the raw `app_name` app-detail rows to the right expanded
 * row — the top-level rollup no longer does this matching client-side.
 */
export const buildBundleIndex = (publishers: RibeyePublisher[]): Map<string, RibeyePublisher> =>
  new Map(publishers.flatMap(publisher => publisher.bundles.map(bundle => [bundle, publisher] as const)));

export const PUBLISHER_INVENTORY_METRICS = [
  'impressions',
  'spend',
  'clicks',
  'reach',
  'conversions',
  'client_spend',
  'video_completes',
  'video_plays',
] as const;

/**
 * Ratio metrics requested directly from the generic report endpoint rather
 * than re-derived client-side (Lucas, review): the server already pools these
 * correctly per row — e.g. `client_cpm` over `priced_impressions`, not every
 * impression — and re-deriving from summed base metrics both undid that
 * pooling and turned a null (GAM spend is NULL by design) into a wrong $0/0%
 * once `toNum` coerced it. Every row this file reads (`ribeye_publisher`
 * buckets and `app_name`+`dsp` rows) is already a single server-side group,
 * so there is always exactly one row's own ratio value to carry through.
 */
const RATIO_METRICS = ['cpm', 'ctr', 'frequency', 'vcr', 'client_cpm'] as const;

type MetricSums = Record<string, number | null>;

/** Preserves null (no value, e.g. GAM spend) instead of coercing it to 0. */
const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** SQL SUM semantics: a metric stays null only while every contributing row's value has been null. */
const addRow = (sums: MetricSums, row: ReportRow): MetricSums => {
  const out = { ...sums };
  for (const key of PUBLISHER_INVENTORY_METRICS) {
    const v = toNum(row[key]);
    if (v !== null) out[key] = (out[key] ?? 0) + v;
  }
  return out;
};

const emptySums = (): MetricSums => Object.fromEntries(PUBLISHER_INVENTORY_METRICS.map(k => [k, null]));

/** Pulls the server's own pre-pooled ratio values off one row, preserving null. */
const extractRatios = (row: ReportRow): Record<string, number | null> =>
  Object.fromEntries(RATIO_METRICS.map(key => [key, toNum(row[key])]));

/**
 * Fallback only for the one case with no single row to read ratios from: a
 * bundle whose raw `app_name`+`dsp` rows carry no `dsp` at all, so
 * `buildAppDetailIndex` has to merge more than one row into that bundle's
 * totals. Approximates `cpm` over all summed impressions rather than only
 * spend-bearing ones (the exact per-row SQL, `SUM(IFF(spend IS NOT NULL,
 * impressions, 0))`, isn't available post-merge) — negligible for
 * Beeswax-mapped bundles, which always carry spend.
 */
export const deriveRatioMetrics = (sums: MetricSums): Record<string, number | null> => {
  const bounded = (num: number, den: number): number | null => (den > 0 ? Math.min(num / den, 1) : null);
  const impressions = sums.impressions ?? 0;
  return {
    cpm: sums.spend != null && impressions > 0 ? (sums.spend / impressions) * 1000 : null,
    ctr: sums.clicks != null ? bounded(sums.clicks, impressions) : null,
    frequency: sums.reach != null && sums.reach > 0 ? impressions / sums.reach : null,
    vcr:
      sums.video_completes != null && sums.video_plays != null ? bounded(sums.video_completes, sums.video_plays) : null,
    client_cpm: sums.client_spend != null && impressions > 0 ? (sums.client_spend / impressions) * 1000 : null,
  };
};

export interface PublisherAppDetail {
  bundle: string;
  dsp: string | null;
  metrics: Record<string, number | null>;
}

export interface PublisherInventoryRow {
  /** Stable key for React + sort/page state. */
  id: string;
  kind: 'publisher' | 'unmapped' | 'unknown';
  name: string;
  logo: string | null;
  /** Apps rolled up under this row (publisher / unknown only) — undefined for a single unmapped app or a domain. */
  appCount?: number;
  /** Per-bundle/DSP breakdown for the expanded row — gated client-side on canViewPlatform (#4442), not fetched conditionally. */
  apps?: PublisherAppDetail[];
  metrics: Record<string, number | null>;
}

const finalize = (
  id: string,
  kind: PublisherInventoryRow['kind'],
  name: string,
  logo: string | null,
  sums: MetricSums,
  ratios: Record<string, number | null>,
  appCount?: number,
  apps?: PublisherAppDetail[],
): PublisherInventoryRow => ({
  id,
  kind,
  name,
  logo,
  appCount,
  apps,
  metrics: { ...sums, ...ratios },
});

/** The two fallback bucket keys ribeye-api's `ribeye_publisher` dimension emits — see publisherRollup.ts. */
const UNMAPPED_BUCKET_KEY = 'unmapped';
export const UNKNOWN_BUCKET_KEY = 'unknown';

/**
 * Builds the per-bundle/DSP breakdown map for an expanded 'publisher' row,
 * keyed by the SAME `publisher-<id>` bucket string the server's
 * `ribeye_publisher` rows carry, from the raw `app_name`+`dsp` rows (still
 * gated by `can_view_raw_inventory`). An unmapped/junk bundle in this raw
 * fetch is skipped — the top-level Unmapped/Unknown bucket rows already
 * carry its totals, and neither is expandable in the UI.
 */
interface AppDetailEntry {
  /** Readable app name, or the bundle id when the name is junk. Keyed by bundle id, not this. */
  label: string;
  sums: MetricSums;
  byDsp: Map<string, { sums: MetricSums; ratios: Record<string, number | null> }>;
}

const TRAILING_STORE_ID = /\(([^()]+)\)\s*$/;

/**
 * Same match order as ribeye-api's publisherRollup.ts `bucketFor` — bundle id,
 * then the store id DV360 appends to the app name, then the name — so an
 * expanded row lists exactly the apps the server rolled into it.
 */
export const publisherForAppRow = (
  row: ReportRow,
  bundleIndex: Map<string, RibeyePublisher>,
): RibeyePublisher | undefined => {
  const storeId = typeof row.app_name === 'string' ? row.app_name.match(TRAILING_STORE_ID)?.[1] : undefined;
  for (const value of [row.app_bundle, storeId, row.app_name]) {
    if (isJunkInventoryName(value)) continue;
    const publisher = bundleIndex.get(String(value));
    if (publisher) return publisher;
  }
  return undefined;
};

const buildAppDetailIndex = (
  appDetailRows: ReportRow[],
  bundleIndex: Map<string, RibeyePublisher>,
): Map<string, Map<string, AppDetailEntry>> => {
  const byBucket = new Map<string, Map<string, AppDetailEntry>>();
  for (const row of appDetailRows) {
    const publisher = publisherForAppRow(row, bundleIndex);
    let bucketKey: string;
    if (publisher) {
      bucketKey = `publisher-${publisher.id}`;
    } else if (row.dsp === 'gam') {
      // Mirrors ribeye-api's publisherRollup.ts `bucketFor` GAM fallback
      // (Matt, ribeye-api#2615) — an uncurated GAM bundle is grouped by the name GAM
      // itself reports, same bucket key shape as the server's totals row, so
      // the expanded breakdown lines up with what's shown at the top level.
      const storeId = typeof row.app_name === 'string' ? row.app_name.match(TRAILING_STORE_ID)?.[1] : undefined;
      const candidates = [row.app_bundle, storeId, row.app_name].filter(v => !isJunkInventoryName(v));
      if (candidates.length === 0) continue;
      const gamLabel = isJunkInventoryName(row.app_name) ? String(candidates[0]) : String(row.app_name);
      bucketKey = `gam-self:${gamLabel}`;
    } else {
      continue;
    }
    // Two bundles can share an app name ("Pluto TV" on Android and iOS), so the
    // bundle id is the key; the name is only what the row displays.
    const appKey = String(isJunkInventoryName(row.app_bundle) ? row.app_name : row.app_bundle);
    const label = String(isJunkInventoryName(row.app_name) ? row.app_bundle : row.app_name);

    let byBundle = byBucket.get(bucketKey);
    if (!byBundle) {
      byBundle = new Map();
      byBucket.set(bucketKey, byBundle);
    }
    let entry = byBundle.get(appKey);
    if (!entry) {
      entry = { label, sums: emptySums(), byDsp: new Map() };
      byBundle.set(appKey, entry);
    }
    // Only touches the DSP-less, merged-bundle fallback below — a dsp-split
    // row's own ratios come straight from that row, never this accumulation.
    entry.sums = addRow(entry.sums, row);
    const dsp = row.dsp === null || row.dsp === undefined ? null : String(row.dsp);
    if (dsp) {
      const prior = entry.byDsp.get(dsp);
      entry.byDsp.set(dsp, { sums: addRow(prior?.sums ?? emptySums(), row), ratios: extractRatios(row) });
    }
  }
  return byBucket;
};

/**
 * Per-bundle detail rows for an expanded Publisher row: one per DSP the
 * bundle actually appeared under (ratios read straight off that row — each
 * app_name+dsp combo is already a single server-side group), or a single
 * DSP-less row when it only ever appeared ungrouped (ratios re-derived from
 * the merged sums, the one case with no single row to read them from).
 */
const appDetailsFrom = (bundle: string, sums: MetricSums, byDsp: AppDetailEntry['byDsp']): PublisherAppDetail[] =>
  byDsp.size > 0
    ? [...byDsp.entries()].map(
        ([dsp, { sums: dspSums, ratios }]): PublisherAppDetail => ({
          bundle,
          dsp,
          metrics: { ...dspSums, ...ratios },
        }),
      )
    : [{ bundle, dsp: null, metrics: { ...sums, ...deriveRatioMetrics(sums) } }];

/**
 * Builds the Publishers tab's rows from the server's pre-grouped
 * `ribeye_publisher` dimension rows (one per Ribeye Publisher, one
 * "Unmapped", one "Unknown / Pending mapping" — already summed and bucketed
 * server-side). Domains are out of scope here — see the module doc comment.
 */
export const rollupPublisherInventory = (
  publisherTotals: ReportRow[],
  appDetailRows: ReportRow[],
  publishers: RibeyePublisher[],
): PublisherInventoryRow[] => {
  const bundleIndex = buildBundleIndex(publishers);
  const appDetailsByBucketKey = buildAppDetailIndex(appDetailRows, bundleIndex);

  const rows: PublisherInventoryRow[] = [];
  // Seeded from the server's own "unknown" row (junk app bundles). In
  // practice this is always exactly one server-side group, same as every
  // other bucket here — derived rather than extracted only because more than
  // one matching row is theoretically possible and would need merging.
  let unknownSums = emptySums();
  let unknownCount = 0;
  // Last-row-wins, same "exactly one in practice" reasoning as unknownSums
  // above — there's no sound way to merge two rows' already-pooled ratios,
  // so this only matters if the theoretical multi-row case ever happens.
  let unknownRatios: Record<string, number | null> | null = null;

  for (const row of publisherTotals) {
    const bucketKey = String(row.ribeye_publisher);

    if (bucketKey === UNKNOWN_BUCKET_KEY) {
      unknownSums = addRow(unknownSums, row);
      unknownCount += toNum(row.app_count) ?? 0;
      unknownRatios = extractRatios(row);
      continue;
    }

    const kind: PublisherInventoryRow['kind'] = bucketKey === UNMAPPED_BUCKET_KEY ? 'unmapped' : 'publisher';
    const name = String(row.ribeye_publisher_name ?? '');
    const logo = (row.ribeye_publisher_logo as string | null) ?? null;
    const appCount = toNum(row.app_count) ?? 0;
    const sums = addRow(emptySums(), row);
    const apps =
      kind === 'publisher'
        ? [...(appDetailsByBucketKey.get(bucketKey)?.values() ?? [])].flatMap(({ label, sums: appSums, byDsp }) =>
            appDetailsFrom(label, appSums, byDsp),
          )
        : undefined;

    rows.push(finalize(bucketKey, kind, name, logo, sums, extractRatios(row), appCount, apps));
  }

  if (unknownCount > 0) {
    rows.push(
      finalize(
        UNKNOWN_BUCKET_KEY,
        'unknown',
        'Unknown / Pending mapping',
        null,
        unknownSums,
        unknownRatios ?? deriveRatioMetrics(unknownSums),
        unknownCount,
      ),
    );
  }

  return rows;
};
